import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ebd_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

// Detecta se está rodando como app (WebView/TWA/PWA) ou no navegador normal
const isRunningAsApp = () => {
  const ua = navigator.userAgent || '';
  // Android WebView: contém "wv" ou "WebView" no user agent
  const isWebView = /; wv\)/.test(ua) || /WebView/.test(ua);
  // PWA standalone
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // iOS standalone
  const isIOSStandalone = window.navigator.standalone === true;
  // TWA
  const isTWA = document.referrer.includes('android-app://');
  return isWebView || isStandalone || isIOSStandalone || isTWA;
};

const PlanosModal = ({ isOpen, onClose, igrejaId, planoAtual, valorMensal, planoAtivoAte, diaVencimento, emAtraso, diasAtraso, onPlanoAtualizado }) => {
  const [processando, setProcessando] = useState(null);
  const isApp = isRunningAsApp();
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  // PIX
  const [pixData, setPixData] = useState(null);
  const [pixStatus, setPixStatus] = useState('aguardando');
  const [copiado, setCopiado] = useState(false);
  // Cartao
  const [mostrarCartao, setMostrarCartao] = useState(false);
  const [cartaoForm, setCartaoForm] = useState({ numero: '', nome: '', validade: '', cvv: '', email: '', parcelas: 1 });
  const [cartaoStatus, setCartaoStatus] = useState(null); // null | 'processando' | 'aprovado' | 'erro'
  const [cartaoErro, setCartaoErro] = useState('');
  const [mpPublicKey, setMpPublicKey] = useState('');

  const pollingRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      carregarPublicKey();
      resetarEstado();
    }
    return () => pararPolling();
  }, [isOpen]);

  const resetarEstado = () => {
    setPixData(null); setPixStatus('aguardando'); setPlanoSelecionado(null);
    setMostrarCartao(false); setCartaoStatus(null); setCartaoErro('');
    setCartaoForm({ numero: '', nome: '', validade: '', cvv: '', email: '', parcelas: 1 });
  };

  const pararPolling = () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };

  const carregarPublicKey = async () => {
    try { const r = await axios.get(`${API}/pagamentos/public-key`); setMpPublicKey(r.data.public_key); } catch (e) { /* ok */ }
  };

  const iniciarPollingStatus = (paymentId) => {
    pararPolling();
    let t = 0;
    pollingRef.current = setInterval(async () => {
      t++;
      if (t > 120) { pararPolling(); return; }
      try {
        const r = await axios.get(`${API}/pagamentos/${paymentId}/status`, { headers: getAuthHeaders() });
        if (r.data.status === 'approved') {
          pararPolling(); setPixStatus('aprovado');
          if (onPlanoAtualizado && igrejaId) {
            try { const p = await axios.get(`${API}/igrejas/${igrejaId}/plano`, { headers: getAuthHeaders() }); onPlanoAtualizado(p.data); } catch (e) {}
          }
        }
      } catch (e) {}
    }, 5000);
  };

  const handlePagarPix = async (planoId) => {
    if (!igrejaId) return;
    setProcessando(planoId);
    try {
      const r = await axios.post(`${API}/pagamentos/criar-pix`, { igreja_id: igrejaId, plano: planoId }, { headers: getAuthHeaders() });
      setPixData(r.data); setPixStatus('aguardando'); setCopiado(false);
      iniciarPollingStatus(r.data.payment_id);
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao gerar PIX'); }
    finally { setProcessando(null); }
  };

  const handlePagarCartao = async (planoId) => {
    if (!igrejaId || !mpPublicKey) return;
    setCartaoStatus('processando'); setCartaoErro('');

    try {
      // Gerar token do cartao usando SDK MP
      const mp = new window.MercadoPago(mpPublicKey);
      const [mes, ano] = cartaoForm.validade.split('/');

      const tokenResult = await mp.createCardToken({
        cardNumber: cartaoForm.numero.replace(/\s/g, ''),
        cardholderName: cartaoForm.nome,
        cardExpirationMonth: mes?.trim(),
        cardExpirationYear: ano?.trim()?.length === 2 ? '20' + ano.trim() : ano?.trim(),
        securityCode: cartaoForm.cvv,
      });

      if (!tokenResult || !tokenResult.id) {
        setCartaoStatus('erro'); setCartaoErro('Dados do cartao invalidos. Verifique e tente novamente.');
        return;
      }

      // Detectar bandeira pelo BIN
      let paymentMethodId = 'visa';
      try {
        const bin = cartaoForm.numero.replace(/\s/g, '').substring(0, 6);
        const pmResp = await axios.get(`https://api.mercadopago.com/v1/payment_methods/search?bins=${bin}&access_token=${mpPublicKey}`);
        if (pmResp.data?.results?.[0]) { paymentMethodId = pmResp.data.results[0].id; }
      } catch (e) {
        // Fallback: detectar pela primeira cifra
        const first = cartaoForm.numero.replace(/\s/g, '')[0];
        if (first === '5') paymentMethodId = 'master';
        else if (first === '3') paymentMethodId = 'amex';
      }

      // Enviar pagamento para o backend
      const r = await axios.post(`${API}/pagamentos/pagar-cartao`, {
        igreja_id: igrejaId,
        plano: planoId,
        token: tokenResult.id,
        payment_method_id: paymentMethodId,
        installments: cartaoForm.parcelas,
        email: cartaoForm.email || 'cliente@ebdad.com.br',
      }, { headers: getAuthHeaders() });

      if (r.data.status === 'approved') {
        setCartaoStatus('aprovado');
        if (onPlanoAtualizado && igrejaId) {
          try { const p = await axios.get(`${API}/igrejas/${igrejaId}/plano`, { headers: getAuthHeaders() }); onPlanoAtualizado(p.data); } catch (e) {}
        }
      } else if (r.data.status === 'in_process' || r.data.status === 'pending') {
        setCartaoStatus('erro'); setCartaoErro('Pagamento em analise. Aguarde a confirmacao.');
      } else {
        setCartaoStatus('erro');
        const detalhe = r.data.status_detail || '';
        if (detalhe.includes('cc_rejected_insufficient_amount')) setCartaoErro('Saldo insuficiente no cartao.');
        else if (detalhe.includes('cc_rejected')) setCartaoErro('Cartao recusado. Tente outro cartao.');
        else setCartaoErro('Pagamento nao aprovado. Tente novamente.');
      }
    } catch (e) {
      setCartaoStatus('erro');
      setCartaoErro(e.response?.data?.detail || 'Erro ao processar pagamento. Verifique os dados.');
    }
  };

  const copiarCodigo = () => {
    if (pixData?.qr_code) { navigator.clipboard.writeText(pixData.qr_code).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 3000); }); }
  };

  const voltarParaPlanos = () => { pararPolling(); resetarEstado(); };

  const formatarNumeroCartao = (v) => {
    const nums = v.replace(/\D/g, '').substring(0, 16);
    return nums.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatarValidade = (v) => {
    const nums = v.replace(/\D/g, '').substring(0, 4);
    if (nums.length > 2) return nums.substring(0, 2) + '/' + nums.substring(2);
    return nums;
  };

  if (!isOpen) return null;

  // NO APP: modal simples sem menção a plano/pagamento/trial
  if (isApp) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden relative">
          <div className="p-6 text-center">
            <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl font-bold">&times;</button>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm mb-6">Acesse pelo navegador para gerenciar sua conta.</p>
            <button
              onClick={() => {
                const url = 'https://ebdad.com.br/?acao=planos';
                try {
                  window.location.href = `intent://${url.replace('https://', '')}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
                } catch (e) {
                  window.open(url, '_system') || window.open(url, '_blank');
                }
              }}
              className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transition-colors"
            >
              Minha Conta
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatarData = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return ''; } };
  const planoJaPago = planoAtual && planoAtual !== 'trial' && planoAtual !== 'free' && planoAtivoAte;

  // Valor configurado pelo administrador do sistema para esta igreja
  const getValorPlano = () => {
    return valorMensal && valorMensal > 0 ? valorMensal : 0;
  };
  const valorConfigurado = getValorPlano();

  // ====== TELA PIX ======
  if (pixData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          {pixStatus === 'aprovado' ? (
            <div className="text-center p-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">Pagamento Confirmado!</h2>
              <p className="text-gray-600 mb-2">Plano <strong>{pixData.plano_nome}</strong> ativado.</p>
              <p className="text-gray-500 text-sm mb-6">R$ {pixData.valor.toFixed(2).replace('.', ',')}</p>
              <button onClick={onClose} className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">Fechar</button>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-5 text-white text-center">
                <h2 className="text-xl font-bold">Pague com PIX</h2>
                <p className="text-cyan-100 text-sm mt-1">Plano {pixData.plano_nome}</p>
                <p className="text-3xl font-extrabold mt-2">R$ {pixData.valor.toFixed(2).replace('.', ',')}</p>
              </div>
              <div className="p-6 text-center">
                {pixData.qr_code_base64 && <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" className="w-48 h-48 mx-auto border-2 border-gray-200 rounded-lg mb-4" />}
                {pixData.qr_code && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Ou copie o codigo PIX:</p>
                    <div className="bg-gray-100 rounded-lg p-3 text-xs text-gray-600 break-all max-h-20 overflow-y-auto mb-2">{pixData.qr_code}</div>
                    <button onClick={copiarCodigo} className={`w-full py-2 rounded-lg font-semibold text-sm ${copiado ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                      {copiado ? 'Codigo copiado!' : 'Copiar codigo PIX'}
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-center mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600 mr-3"></div>
                  <span className="text-yellow-800 text-sm font-medium">Aguardando pagamento...</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">Confirma automaticamente apos o pagamento</p>
                <button onClick={voltarParaPlanos} className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline">Voltar</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ====== TELA CARTAO - APROVADO ======
  if (cartaoStatus === 'aprovado') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">Pagamento Confirmado!</h2>
          <p className="text-gray-600 mb-6">Seu plano foi ativado com sucesso.</p>
          <button onClick={onClose} className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">Fechar</button>
        </div>
      </div>
    );
  }

  // ====== TELA CARTAO - FORMULARIO ======
  if (mostrarCartao && planoSelecionado) {
    const valor = valorConfigurado;
    const nomePlano = planoSelecionado.charAt(0).toUpperCase() + planoSelecionado.slice(1);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white text-center">
            <h2 className="text-xl font-bold">Cartao de Credito</h2>
            <p className="text-purple-100 text-sm mt-1">Plano {nomePlano}</p>
            <p className="text-3xl font-extrabold mt-2">R$ {valor.toFixed(2).replace('.', ',')}</p>
          </div>
          <div className="p-6">
            {cartaoErro && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{cartaoErro}</div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numero do cartao</label>
                <input type="text" placeholder="0000 0000 0000 0000" value={cartaoForm.numero}
                  onChange={(e) => setCartaoForm({...cartaoForm, numero: formatarNumeroCartao(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" maxLength={19} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome no cartao</label>
                <input type="text" placeholder="NOME COMO NO CARTAO" value={cartaoForm.nome}
                  onChange={(e) => setCartaoForm({...cartaoForm, nome: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Validade</label>
                  <input type="text" placeholder="MM/AA" value={cartaoForm.validade}
                    onChange={(e) => setCartaoForm({...cartaoForm, validade: formatarValidade(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" maxLength={5} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                  <input type="text" placeholder="123" value={cartaoForm.cvv}
                    onChange={(e) => setCartaoForm({...cartaoForm, cvv: e.target.value.replace(/\D/g, '').substring(0, 4)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" maxLength={4} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" placeholder="seu@email.com" value={cartaoForm.email}
                  onChange={(e) => setCartaoForm({...cartaoForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
            </div>
            <button
              onClick={() => handlePagarCartao(planoSelecionado)}
              disabled={cartaoStatus === 'processando' || !cartaoForm.numero || !cartaoForm.nome || !cartaoForm.validade || !cartaoForm.cvv}
              className="w-full mt-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center"
            >
              {cartaoStatus === 'processando' ? (
                <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Processando...</>
              ) : `Pagar R$ ${valor.toFixed(2).replace('.', ',')}`}
            </button>
            <button onClick={() => { setMostrarCartao(false); setCartaoErro(''); setCartaoStatus(null); }} className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 underline text-center">Voltar</button>
          </div>
        </div>
      </div>
    );
  }

  // ====== TELA METODO DE PAGAMENTO ======
  if (planoSelecionado) {
    const valor = valorConfigurado;
    const nomePlano = planoSelecionado.charAt(0).toUpperCase() + planoSelecionado.slice(1);

    // NO BROWSER: pagamento normal PIX/Cartao
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white text-center">
            <h2 className="text-xl font-bold">Plano {nomePlano}</h2>
            <p className="text-3xl font-extrabold mt-2">R$ {valor.toFixed(2).replace('.', ',')}<span className="text-sm opacity-80">/mes</span></p>
          </div>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Como deseja pagar?</h3>
            <div className="space-y-3">
              <button onClick={() => handlePagarPix(planoSelecionado)} disabled={processando}
                className="w-full flex items-center p-4 border-2 border-cyan-200 rounded-xl hover:border-cyan-400 hover:bg-cyan-50 transition-colors disabled:opacity-50">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl">&#9889;</span>
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-gray-800">PIX</p>
                  <p className="text-sm text-gray-500">Pagamento instantaneo com QR Code</p>
                </div>
                {processando && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600"></div>}
              </button>

              <button onClick={() => setMostrarCartao(true)}
                className="w-full flex items-center p-4 border-2 border-purple-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-colors">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-7 h-7 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" strokeWidth="2"/><line x1="1" y1="10" x2="23" y2="10" strokeWidth="2"/>
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-gray-800">Cartao de Credito</p>
                  <p className="text-sm text-gray-500">Pague com cartao de credito</p>
                </div>
              </button>
            </div>
            <button onClick={voltarParaPlanos} className="mt-6 w-full text-center text-sm text-gray-500 hover:text-gray-700 underline">Voltar aos planos</button>
          </div>
        </div>
      </div>
    );
  }

  // ====== TELA DO PLANO ATIVO ======
  const planoNome = planoAtual ? planoAtual.charAt(0).toUpperCase() + planoAtual.slice(1) : 'Nenhum';
  const isTrial = planoAtual === 'trial';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden relative">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white text-center relative">
          <h2 className="text-2xl font-bold">Meu Plano</h2>
          <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold">&times;</button>
        </div>

        <div className="p-6">
          {/* Alerta de atraso */}
          {emAtraso && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">⚠️</span>
                </div>
                <div>
                  <p className="font-bold text-red-700">Pagamento em Atraso!</p>
                  <p className="text-red-600 text-sm">
                    {diasAtraso > 0 ? `${diasAtraso} dia${diasAtraso > 1 ? 's' : ''} de atraso` : 'Vencido'}
                    {diaVencimento ? ` — vencimento dia ${diaVencimento}` : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Plano atual */}
          <div className="text-center mb-6">
            <div className={`inline-block px-4 py-1 rounded-full text-sm font-bold mb-3 ${
              emAtraso ? 'bg-red-100 text-red-800 animate-pulse' : 'bg-emerald-100 text-emerald-800'
            }`}>
              {emAtraso ? 'EM ATRASO' : planoJaPago ? 'ATIVO' : isTrial ? 'AVALIACAO' : 'PENDENTE'}
            </div>
            <h3 className="text-2xl font-bold text-gray-800">Plano {planoNome}</h3>
            {(planoAtivoAte || (emAtraso && diaVencimento)) && (
              <p className={`text-sm mt-1 ${emAtraso ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                {emAtraso
                  ? `Venceu em ${diaVencimento
                      ? `${String(diaVencimento).padStart(2,'0')}/${String(new Date().getMonth()+1).padStart(2,'0')}/${new Date().getFullYear()}`
                      : formatarData(planoAtivoAte)}`
                  : `Valido ate ${formatarData(planoAtivoAte)}`}
              </p>
            )}
            {diaVencimento && !emAtraso && (
              <p className="text-gray-400 text-xs mt-1">Vencimento todo dia {diaVencimento}</p>
            )}
            {isTrial && (
              <p className="text-orange-600 text-sm mt-1 font-medium">Periodo de avaliacao</p>
            )}
          </div>

          {/* Valor */}
          <div className="bg-gray-50 rounded-xl p-5 text-center mb-6">
            {valorConfigurado > 0 ? (
              <>
                <p className="text-gray-500 text-sm mb-1">Valor mensal</p>
                <p className="text-4xl font-extrabold text-gray-800">
                  R$ {valorConfigurado.toFixed(2).replace('.', ',')}
                  <span className="text-base font-normal text-gray-500">/mes</span>
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-500 text-sm mb-1">Valor mensal</p>
                <p className="text-lg font-semibold text-gray-400">Consulte o administrador</p>
              </>
            )}
          </div>

          {/* Botao pagar */}
          {valorConfigurado > 0 && emAtraso ? (
            <button
              onClick={() => setPlanoSelecionado(planoAtual && planoAtual !== 'trial' && planoAtual !== 'free' ? planoAtual : 'basico')}
              className="w-full py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors animate-pulse"
            >
              Pagar Agora — {diasAtraso} dia{diasAtraso > 1 ? 's' : ''} em atraso
            </button>
          ) : valorConfigurado > 0 && (!planoJaPago || isTrial) ? (
            <button
              onClick={() => setPlanoSelecionado(planoAtual && planoAtual !== 'trial' && planoAtual !== 'free' ? planoAtual : 'basico')}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
            >
              {isTrial ? 'Assinar Agora' : 'Pagar'}
            </button>
          ) : valorConfigurado > 0 && planoJaPago ? (
            <button
              onClick={() => setPlanoSelecionado(planoAtual)}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
            >
              Renovar Plano
            </button>
          ) : (
            <div className="text-center py-3 px-4 rounded-lg bg-gray-100 text-gray-500 font-medium text-sm">
              Entre em contato com o administrador para ativar seu plano
            </div>
          )}

          <p className="mt-4 text-center text-xs text-gray-400">Pague com PIX ou cartao de credito</p>
        </div>
      </div>
    </div>
  );
};

export default PlanosModal;
